import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { I18nextProvider } from "react-i18next";
import { Toaster } from "sonner";
import { Plane } from "lucide-react";
import { AdProvider, useAdConfig, SplashScreen } from "@tabipla/components";
import i18n from "./infrastructure/i18n";
import { ApiClientProvider } from "./infrastructure/api";
import { HomePage } from "./presentation/pages/Home";
import { Landing } from "./presentation/pages/Landing";
import { Trips } from "./presentation/pages/Trips";
import { TripDetail } from "./presentation/pages/TripDetail";
import { Groups } from "./presentation/pages/Groups";
import { GroupDetail } from "./presentation/pages/GroupDetail";
import { JoinGroup } from "./presentation/pages/JoinGroup";
import { Booklet } from "./presentation/pages/Booklet";
import { SettingsPage } from "./presentation/pages/Settings";
import { ProfilePage } from "./presentation/pages/Profile";
import { FriendsPage } from "./presentation/pages/Friends";
import { LoginPage } from "./presentation/pages/Login";
import { RegisterPage } from "./presentation/pages/Register";
import { useAuth } from "./application/hooks/useAuth";
import { OfflineBanner } from "./presentation/components/OfflineBanner";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 60 * 24, // 24 hours - keep in cache for persistence
      retry: 1,
    },
  },
});

// Persist query cache to localStorage for offline support
const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: "tabitto-query-cache",
});

// Tabitto テーマ
const tabittoSplashTheme = {
  backgroundGradient: "from-blue-50 to-white dark:from-blue-950 dark:to-gray-900",
  iconBg: "bg-blue-500",
  iconText: "text-white",
  progressColor: "bg-blue-500",
};

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Shows Landing for unauthenticated users, HomePage for authenticated users
function ConditionalHomeRoute() {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <HomePage /> : <Landing />;
}

function AppWithAds({ children }: { children: React.ReactNode }) {
  const adConfig = useAdConfig({
    publisherId: import.meta.env.VITE_ADSENSE_PUBLISHER_ID,
    enabled: import.meta.env.VITE_ADS_ENABLED,
    testMode: import.meta.env.VITE_ADS_TEST_MODE,
  });

  return <AdProvider config={adConfig}>{children}</AdProvider>;
}

function AppContent() {
  const { isLoading } = useAuth();

  return (
    <SplashScreen
      appName="Tabitto"
      tagline="旅行計画を、もっと楽しく"
      icon={<Plane className="h-8 w-8" />}
      theme={tabittoSplashTheme}
      isLoading={isLoading}
    >
      <Routes>
        {/* Home Route - Shows Landing or HomePage based on auth */}
        <Route path="/" element={<ConditionalHomeRoute />} />

        {/* Protected Routes */}
        <Route
          path="/trips"
          element={
            <ProtectedRoute>
              <Trips />
            </ProtectedRoute>
          }
        />
        <Route
          path="/trips/:id"
          element={
            <ProtectedRoute>
              <TripDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/groups"
          element={
            <ProtectedRoute>
              <Groups />
            </ProtectedRoute>
          }
        />
        <Route
          path="/groups/:id"
          element={
            <ProtectedRoute>
              <GroupDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/friends"
          element={
            <ProtectedRoute>
              <FriendsPage />
            </ProtectedRoute>
          }
        />

        {/* Public Routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />

        {/* Open Routes (accessible to everyone) */}
        <Route path="/join/:code" element={<JoinGroup />} />
        <Route path="/booklet/:shareCode" element={<Booklet />} />
      </Routes>
    </SplashScreen>
  );
}

export function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <ApiClientProvider>
        <AppWithAds>
          <PersistQueryClientProvider
            client={queryClient}
            persistOptions={{
              persister,
              maxAge: 1000 * 60 * 60 * 24, // 24 hours
              dehydrateOptions: {
                shouldDehydrateQuery: (query) => {
                  // Only persist successful queries for trips and groups
                  const queryKey = query.queryKey[0];
                  if (typeof queryKey !== "string") return false;
                  const persistableKeys = ["trips", "groups", "trip", "group"];
                  return (
                    query.state.status === "success" &&
                    persistableKeys.some((key) => queryKey.startsWith(key))
                  );
                },
              },
            }}
          >
            <OfflineBanner />
            <Toaster
              position="top-center"
              toastOptions={{
                className: "font-sans",
                style: {
                  padding: "12px 16px",
                  borderRadius: "12px",
                },
              }}
              richColors
              closeButton
            />
            <BrowserRouter>
              <AppContent />
            </BrowserRouter>
          </PersistQueryClientProvider>
        </AppWithAds>
      </ApiClientProvider>
    </I18nextProvider>
  );
}
